/**
 * "auto" follows the page the widget sits on, not the visitor's OS: its
 * background, else its declared `color-scheme`, and the OS only for "light dark".
 */
export function hostPageIsDark(doc: Document = document): boolean {
  for (const element of [doc.body, doc.documentElement]) {
    if (!element) continue;
    const rgba = parseRgba(getComputedStyle(element).backgroundColor);
    if (rgba && rgba[3] > 0.5) return luminance(rgba) < 0.5;
  }
  const scheme = getComputedStyle(doc.documentElement).colorScheme ?? "";
  const dark = /\bdark\b/.test(scheme);
  const light = /\blight\b/.test(scheme);
  if (dark && light) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return dark;
}

/**
 * Any computed colour as RGBA. `rgb()` is read directly; anything else
 * (`oklch()`, `color(srgb …)`) goes through a 1x1 canvas, which speaks them all.
 */
function parseRgba(value: string): [number, number, number, number] | null {
  if (!value.startsWith("rgb")) return paintedRgba(value);
  const parts = value.match(/[\d.]+%?/g);
  if (!parts || parts.length < 3) return null;
  const alpha = parts[3]
    ? parts[3].endsWith("%")
      ? Number.parseFloat(parts[3]) / 100
      : Number(parts[3])
    : 1;
  return [Number(parts[0]), Number(parts[1]), Number(parts[2]), alpha];
}

function luminance([r, g, b]: [number, number, number, number]): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

let probe: CanvasRenderingContext2D | null | undefined;

function paintedRgba(value: string): [number, number, number, number] | null {
  if (probe === undefined) {
    probe =
      typeof document === "undefined"
        ? null
        : document
            .createElement("canvas")
            .getContext("2d", { willReadFrequently: true });
  }
  if (!probe) return null;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = "rgba(0, 0, 0, 0)";
  probe.fillStyle = value;
  probe.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0, a = 0] = probe.getImageData(0, 0, 1, 1).data;
  return [r, g, b, a / 255];
}
